// Les salles sur un seul écran (D-138, maquettes s1 à s5, c1, v1 à v3, c2), logique pure et testée (semaine.test.ts) :
//   * la semaine du club : une ligne par jour, une colonne par salle (créneaux des équipes, libres, LIBÉRÉS cette fois,
//     en plus, perdus, matchs, séances hors créneau ; pendant des vacances en 3 étapes : les créneaux habituels gardés ou
//     retirés, les ajouts, les salles fermées, les jours fériés) ;
//   * la semaine montrée : celle en cours jusqu'au mercredi, la suivante dès le jeudi (comme D-123) ;
//   * l'à-faire du responsable des salles et le « À décider » du coach ;
//   * le bandeau des vacances : N semaines avant (4, 6 ou 8), étape 1, 2 ou 3 ;
//   * les vacances en 3 étapes : l'état de chaque salle, ce qui est prérempli, ce que garde un coach, ce qui reste à
//     arbitrer (les ajouts et les chevauchements ; les créneaux habituels sont acceptés d'office).
// Les mêmes règles qu'en base (migration 20261104500000_salles_semaine.sql : _hall_vac_state, _hall_vac_valid).
import { addDays, isoDay } from './salles';
import {
  agendaDay, clock, h, hShort, hallHead, lostAt, periodOfDay, sportHalls, teamColors, teamShort, toMin, WD3, WEEKDAYS,
  type Board, type HolidayRow, type Period, type Range, type Wish,
} from './creneaux';
import type { HolidayKey } from './vacances';

// --- Ce que la base renvoie (hall_week, hall_vac_view) ------------------------------------------------------------------
export interface CalInfo {
  academy: string | null; zone: 'A' | 'B' | 'C' | null; confirmed_at: string | null; by: string | null; read_at: string | null; read_academy: string | null;
  feries_zone: string | null; banner_weeks: number; wishes_days: number;
}
export type WHoliday = HolidayRow & { source?: 'officiel' | 'secours' };
export interface WPeriod extends Period { prefill?: boolean; sent_at?: string | null; answered?: number }
export type HallState = 'open' | 'closed' | 'apart';
export interface PHall { period_id?: string; hall_id: string; state: HallState; note: string | null; open_days: string[] }
export interface VWish extends Wish { period_id?: string; habit: boolean; slot_id: string | null; series_id: string | null }
export interface Ferie { day: string; label: string; source?: string }
export interface Week extends Omit<Board, 'holidays' | 'periods'> {
  holidays: WHoliday[]; periods: WPeriod[]; feries: Ferie[]; calendar: CalInfo; phalls: PHall[]; vwishes: VWish[];
}
export interface VTeam { id: string; name: string; category: string | null; gender: string; mine: boolean; coach: string | null; expected: boolean; answered_at: string | null }
export interface VView {
  manage: boolean; bureau: boolean; today: string; holiday: WHoliday | null; period: (WPeriod & { sent_by?: string | null }) | null;
  feries: Ferie[]; phalls: PHall[]; ranges: Range[]; wishes: VWish[]; teams: VTeam[]; reminded: string | null;
}
/** Une séance qui ne colle à aucun créneau (D-135, hall_plan_check) : ce que la grille en montre. */
export interface OffItem { event_id: string; team_id: string; hall_id: string | null; weekly: boolean; weekday: number; day: string; starts: string; ends: string }

// --- Mots ---------------------------------------------------------------------------------------------------------------
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MO = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const num = (d: string) => { const n = Number(d.slice(8, 10)); return n === 1 ? '1er' : String(n); };
/** « mer. 4 nov. » */
export const sdm = (d: string) => `${WD3[isoDay(d) - 1]!.toLowerCase()}. ${num(d)} ${MO[Number(d.slice(5, 7)) - 1]}`;
/** « Mer. 4 » */
export const sd = (d: string) => `${WD3[isoDay(d) - 1]}. ${num(d)}`;
/** « Semaine du 2 au 8 novembre », « Semaine du 28 déc. au 3 janv. » */
export function weekLabel(monday: string): string {
  const z = addDays(monday, 6);
  return monday.slice(0, 7) === z.slice(0, 7) ? `Semaine du ${num(monday)} au ${num(z)} ${MONTHS[Number(z.slice(5, 7)) - 1]}`
    : `Semaine du ${num(monday)} ${MO[Number(monday.slice(5, 7)) - 1]} au ${num(z)} ${MO[Number(z.slice(5, 7)) - 1]}`;
}
/** « 2–8 nov. », « 28 déc.–3 janv. » (les puces de la version large). */
export function weekChip(monday: string): string {
  const z = addDays(monday, 6);
  return monday.slice(0, 7) === z.slice(0, 7) ? `${num(monday)}–${num(z)} ${MO[Number(z.slice(5, 7)) - 1]}`
    : `${num(monday)} ${MO[Number(monday.slice(5, 7)) - 1]}–${num(z)} ${MO[Number(z.slice(5, 7)) - 1]}`;
}
/** « dans 7 semaines », « dans 5 jours », « demain », « en cours ». */
export function inWeeks(today: string, day: string): string {
  const n = Math.round((Date.parse(day) - Date.parse(today)) / 86400000);
  if (n <= 0) return 'en cours';
  if (n === 1) return 'demain';
  if (n < 14) return `dans ${n} jours`;
  return `dans ${Math.round(n / 7)} semaines`;
}
/** « Noël », « la Toussaint », « hiver »… pour les titres courts. */
export const HOL_SHORT: Record<HolidayKey, string> = { toussaint: 'Toussaint', noel: 'Noël', hiver: 'Hiver', printemps: 'Printemps', ete: 'Été' };
/** « Jour de Noël » → « Noël » (la ligne d'un jour férié dans la grille). */
export const ferieShort = (label: string) => label.replace(/^Jour de /i, '').replace(/^2ème jour de Noël$/i, 'Saint-Étienne');

// --- La semaine montrée -------------------------------------------------------------------------------------------------
export const mondayOf = (d: string) => addDays(d, 1 - isoDay(d));
/** La semaine en cours jusqu'au mercredi, la suivante à partir du jeudi. */
export const defaultMonday = (today: string) => (isoDay(today) >= 4 ? addDays(mondayOf(today), 7) : mondayOf(today));

// --- Les vacances en 3 étapes : l'état d'une salle un jour donné (comme _hall_vac_state) --------------------------------
export function vacState(phalls: PHall[], feries: Ferie[], periodId: string | null, hallId: string, day: string): HallState {
  const row = phalls.find(x => (x.period_id ?? periodId) === periodId && x.hall_id === hallId);
  if (feries.some(f => f.day === day) && !(row?.open_days ?? []).includes(day)) return 'closed';
  return row?.state ?? 'open';
}
/** Un créneau habituel a-t-il lieu ce jour-là ? (comme _hall_vac_valid) */
export function vacValid(phalls: PHall[], ranges: Range[], feries: Ferie[], periodId: string | null, hallId: string, day: string, a: string, z: string): boolean {
  const st = vacState(phalls, feries, periodId, hallId, day);
  if (st === 'open') return true;
  if (st === 'closed') return false;
  return ranges.some(r => (r.period_id ?? periodId) === periodId && r.hall_id === hallId && !r.closed && r.starts && r.ends && r.weekdays.includes(isoDay(day))
    && r.starts.slice(0, 5) <= a && r.ends.slice(0, 5) >= z && (!r.from_day || day >= r.from_day) && (!r.to_day || day <= r.to_day));
}

// --- La grille de la semaine ----------------------------------------------------------------------------------------------
export interface Cell {
  col: string; start: string; end: string; cls: string; label: string; sub: string; color?: string; mine?: boolean; teamId?: string | null;
  slotId?: string; extraId?: string; eventId?: string; lossId?: string; askId?: string; wishId?: string; day: string; opp?: string;
}
export interface GridCol { id: string; main: string; sub: string; manager: string | null; off: boolean }
export interface Grid {
  monday: string; days: string[]; cols: GridCol[]; cells: Record<string, Cell[]>;
  /** Un jour férié (son nom court) ; `allClosed` : aucune salle ce jour-là (la ligne « Noël · aucune salle »). */
  ferie: Record<string, { label: string; allClosed: boolean }>;
  closed: Set<string>;   // « jour|salle » fermée (vacances en 3 étapes)
  hot: Set<string>;      // un jour avec un créneau perdu
  period: WPeriod | null;
}
const overlap = (a: string, z: string, b: string, y: string) => toMin(a) < toMin(y) && toMin(b) < toMin(z);

/** La semaine du club (s1, c1, v2) : `coach` estompe ce qui n'est pas à lui ; `off` : les séances hors créneau (D-135). */
export function weekGrid(w: Week, monday: string, opt: { coach?: boolean; off?: OffItem[] } = {}): Grid {
  const b = w as unknown as Board;
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const colors = teamColors(w.teams);
  const team = (id: string | null | undefined) => (id ? w.teams.find(t => t.id === id) ?? null : null);
  const short = (id: string | null | undefined) => { const t = team(id); return t ? teamShort(t.name) : ''; };
  const cells: Record<string, Cell[]> = {};
  const ferie: Grid['ferie'] = {};
  const closed = new Set<string>(), hot = new Set<string>();
  const used = new Set<string>();
  const halls = sportHalls(w);
  let shownPeriod: WPeriod | null = null;
  for (const day of days) {
    const out: Cell[] = [];
    const dp = periodOfDay({ holidays: w.holidays, periods: w.periods }, day);
    const p = dp.period as WPeriod | null;
    const fer = w.feries.find(f => f.day === day);
    if (p?.prefill) {
      shownPeriod = p;
      // D-138 : les vacances en 3 étapes, d'après les souhaits (créneaux habituels gardés, retirés, ajouts).
      for (const x of halls) if (vacState(w.phalls, w.feries, p.id, x.id, day) === 'closed') closed.add(`${day}|${x.id}`);
      const ws = w.vwishes.filter(v => (v.period_id ?? p.id) === p.id && (v.f_day ?? v.day) === day && v.status !== 'refused' && !(v.status === 'withdrawn' && !v.habit));
      const live = ws.filter(v => v.status !== 'withdrawn');
      for (const v of ws) {
        const hall = v.f_hall ?? v.hall_id;
        if (!hall) continue;
        const a = (v.f_starts ?? v.starts ?? '09:00').slice(0, 5), z = (v.f_ends ?? v.ends ?? '17:00').slice(0, 5);
        const t = team(v.team_id);
        const clash = live.some(o => o.id !== v.id && (o.f_hall ?? o.hall_id) === hall && overlap(a, z, (o.f_starts ?? o.starts ?? '09:00').slice(0, 5), (o.f_ends ?? o.ends ?? '17:00').slice(0, 5))
          && (o.status === 'sent' || v.status === 'sent'));
        if (v.habit && v.status === 'withdrawn') {
          out.push({ day, col: hall, start: a, end: z, cls: 'freed', label: 'Libéré', sub: 'retiré', color: colors.get(v.team_id), teamId: v.team_id, wishId: v.id, mine: !!t?.mine });
        } else {
          out.push({ day, col: hall, start: a, end: z, cls: `team${v.habit ? '' : v.status === 'sent' ? ' add' : ''}${clash ? ' clash' : ''}`, label: short(v.team_id),
            sub: v.all_day && v.status === 'sent' ? 'journée' : `${hShort(a)}–${hShort(z)}`, color: colors.get(v.team_id), teamId: v.team_id, wishId: v.id, mine: !!t?.mine });
        }
      }
      if (fer) ferie[day] = { label: ferieShort(fer.label), allClosed: halls.every(x => closed.has(`${day}|${x.id}`)) && !out.length };
    } else {
      if (fer) ferie[day] = { label: ferieShort(fer.label), allClosed: false };
      const ag = agendaDay(b, day);
      for (const x of ag.blocks) {
        const c: Cell = { day, col: x.col, start: x.start, end: x.end, cls: x.kind, label: x.label, sub: x.sub, color: x.color, mine: !!x.mine, teamId: x.teamId,
          slotId: x.slotId, extraId: x.extraId, eventId: x.eventId, lossId: x.lossId };
        if (x.kind === 'range') { c.cls = 'free'; c.label = 'Ouvert'; }
        if (x.kind === 'team') {
          // Une séance annulée cette fois : le créneau est libéré (pour les autres), « annulée » pour soi.
          const off = w.occ.find(o => o.day === day && o.hall_id === x.col && o.team_id === x.teamId && o.cancelled && o.kind !== 'match'
            && overlap(clock(o.starts), clock(o.ends), x.start, x.end));
          if (off) {
            if (opt.coach && x.mine) { c.cls = 'team cancel'; c.sub = 'annulée'; }
            else { c.cls = 'freed'; c.label = 'Libéré'; c.sub = opt.coach ? 'à prendre' : `${hShort(x.start)}–${hShort(x.end)}`; }
          }
        }
        if (x.kind === 'free' || (x.kind === 'extra' && !x.teamId && x.label === 'Libre')) {
          const ask = w.asks.find(a => (a.slot_id === x.slotId || (x.extraId && a.extra_id === x.extraId)) && a.status === 'pending');
          c.label = 'Libre';
          if (ask) { c.cls = 'asked'; c.sub = ask.mine ? 'demandé' : '1 demande'; c.askId = ask.id; }
          else { c.cls = x.kind === 'extra' ? 'extra' : 'free'; c.sub = `${hShort(x.start)}–${hShort(x.end)}`; }
        } else if (x.kind === 'extra') {
          c.label = x.teamId ? short(x.teamId) : 'En plus'; c.sub = `${hShort(x.start)}–${hShort(x.end)}`;
        }
        if (x.kind === 'lost') { c.label = 'Perdu'; c.sub = short(x.teamId) || 'créneau'; c.mine = !!team(x.teamId)?.mine; hot.add(day); }
        if (x.kind === 'match') {
          const lost = lostAt(w.losses, x.col, day);
          const [who, opp] = x.label.split(' – ');
          c.label = who ?? x.label; c.sub = lost ? 'à déplacer' : `match ${hShort(x.start)}`;
          if (lost) { c.cls = 'match hit'; hot.add(day); }
          c.opp = opp;
        }
        if (x.kind === 'session' && opt.off?.some(o => o.event_id === x.eventId)) { c.cls = 'session off'; c.sub = 'hors créneau'; }
        out.push(c);
      }
      // Les séances hors créneau d'une série (D-135) : la grille les montre aussi.
      for (const o of opt.off ?? []) {
        if (!o.hall_id || !o.weekly || o.weekday !== isoDay(day) || day < o.day || out.some(c => c.eventId === o.event_id)) continue;
        if (!halls.some(x => x.id === o.hall_id)) continue;
        out.push({ day, col: o.hall_id, start: o.starts.slice(0, 5), end: o.ends.slice(0, 5), cls: 'session off', label: short(o.team_id), sub: 'hors créneau',
          color: colors.get(o.team_id), teamId: o.team_id, eventId: o.event_id, mine: !!team(o.team_id)?.mine });
      }
      for (const o of opt.off ?? []) {
        if (!o.hall_id || o.weekly || o.day !== day || out.some(c => c.eventId === o.event_id) || !halls.some(x => x.id === o.hall_id)) continue;
        out.push({ day, col: o.hall_id, start: o.starts.slice(0, 5), end: o.ends.slice(0, 5), cls: 'session off', label: short(o.team_id), sub: 'hors créneau',
          color: colors.get(o.team_id), teamId: o.team_id, eventId: o.event_id, mine: !!team(o.team_id)?.mine });
      }
    }
    // Les matchs à domicile et les créneaux perdus pendant des vacances en 3 étapes aussi.
    if (p?.prefill) {
      for (const o of w.occ.filter(o => o.day === day && o.kind === 'match' && !o.cancelled && halls.some(x => x.id === o.hall_id))) {
        const t = team(o.team_id), sq = t?.squads.find(q => q.id === o.squad_id)?.name;
        const lost = lostAt(w.losses, o.hall_id, day);
        out.push({ day, col: o.hall_id, start: clock(o.starts), end: clock(o.ends), cls: lost ? 'match hit' : 'match', label: sq ?? short(o.team_id),
          sub: lost ? 'à déplacer' : `match ${hShort(clock(o.starts))}`, color: colors.get(o.team_id), teamId: o.team_id, eventId: o.event_id, mine: !!t?.mine });
        if (lost) hot.add(day);
      }
    }
    out.sort((a, z) => a.start.localeCompare(z.start));
    for (const c of out) used.add(c.col);
    cells[day] = out;
  }
  // Les colonnes : les salles de sport qui ont quelque chose cette semaine (ou des créneaux), dans l'ordre des gestionnaires.
  const withSlots = new Set(w.slots.map(s => s.hall_id));
  const cols: GridCol[] = halls.filter(x => used.has(x.id) || withSlots.has(x.id)).map(x => {
    const m = w.managers.find(y => y.id === x.manager_id);
    const inPeriod = days.filter(d => !!shownPeriod && d >= shownPeriod.starts && d < shownPeriod.back);
    return { id: x.id, ...hallHead(x.name), manager: m?.name ?? null,
      off: inPeriod.length > 0 && inPeriod.every(d => closed.has(`${d}|${x.id}`)) && inPeriod.some(d => !w.feries.some(f => f.day === d)) };
  });
  return { monday, days, cols, cells, ferie, closed, hot, period: shownPeriod };
}

/** La légende de ce que la grille montre (équipes, puis les sortes de cases). */
export function legendOf(w: Pick<Week, 'teams'>, g: Grid): { teams: { id: string; label: string; color: string }[]; kinds: string[] } {
  const colors = teamColors(w.teams);
  const all = Object.values(g.cells).flat();
  const ids = new Set(all.filter(c => c.teamId && /team|session|extra|freed/.test(c.cls)).map(c => c.teamId!));
  const order = [...colors.keys()];
  const teams = w.teams.filter(t => ids.has(t.id)).sort((a, z) => order.indexOf(a.id) - order.indexOf(z.id))
    .map(t => ({ id: t.id, label: teamShort(t.name), color: colors.get(t.id)! }));
  const has = (k: string) => all.some(c => c.cls.split(' ').includes(k));
  const kinds = [has('free') || has('asked') ? 'free' : '', has('freed') ? 'freed' : '', has('extra') ? 'extra' : '', has('lost') ? 'lost' : '',
    has('match') ? 'match' : '', has('off') ? 'off' : '', has('add') ? 'add' : '', has('clash') ? 'clash' : ''].filter(Boolean);
  return { teams, kinds };
}

// --- Le bandeau des vacances --------------------------------------------------------------------------------------------
export interface Banner {
  holiday: WHoliday; period: WPeriod | null; step: 1 | 2 | 3; when: string; href: string;
  /** Étape 2 : combien de coachs ont confirmé, sur combien. */
  answered: number; expected: number; adds: number;
}
/** Les prochaines vacances, N semaines avant (4, 6 ou 8) ou déjà ouvertes en 3 étapes, tant qu'elles ne sont pas publiées. */
export function banner(w: Pick<Week, 'holidays' | 'periods' | 'calendar' | 'today' | 'vwishes'>): Banner | null {
  const weeks = w.calendar?.banner_weeks ?? 6;
  const next = [...(w.holidays ?? [])].filter(h => h.starts > w.today).sort((a, z) => a.starts.localeCompare(z.starts));
  for (const h of next) {
    const p = (w.periods.find(x => x.key === h.key && x.season === h.season) ?? null) as WPeriod | null;
    if (p && (p.status === 'published' || (!p.prefill && p.mode !== 'same'))) continue;
    const due = addDays(h.starts, -7 * weeks) <= w.today;
    if (!due && !p?.prefill) break;
    const step: 1 | 2 | 3 = !p?.prefill || !p.sent_at ? 1 : p.wishes_until && w.today > p.wishes_until ? 3 : 2;
    return { holiday: h, period: p, step, when: inWeeks(w.today, h.starts), href: `#/salles/vacances/${h.key}/${h.season}`,
      answered: p?.answered ?? 0, expected: p?.expected ?? 0, adds: p?.open ?? 0 };
  }
  return null;
}
/** « Du sam. 19 déc. au dim. 3 janv. » (le dernier jour des vacances, la veille de la reprise). */
export const holidayRange = (h: { starts: string; back: string }) => `Du ${sdm(h.starts)} au ${sdm(addDays(h.back, -1))}`;

// --- L'à-faire du responsable des salles --------------------------------------------------------------------------------
export interface TodoItem { key: string; icon: 'ask' | 'loss' | 'off' | 'request' | 'period' | 'zone'; title: string; sub: string; href?: string; askId?: string; lossId?: string }
export interface RequestLite { id: string; hall: string; day: string; starts: string; who: string | null; status: string; purpose: string; purpose_text: string | null }
const PURPOSE_FOR: Record<string, string> = { pot: 'pour un pot', reunion: 'pour une réunion', anniversaire: 'pour un anniversaire', autre: '' };
export function todoItems(w: Week, reqs: RequestLite[], off: OffItem[]): TodoItem[] {
  if (!w.manage) return [];
  const out: TodoItem[] = [];
  const team = (id: string) => w.teams.find(t => t.id === id);
  const hall = (id: string) => w.halls.find(x => x.id === id)?.name ?? 'Salle';
  for (const a of w.asks.filter(x => x.status === 'pending')) {
    out.push({ key: `a:${a.id}`, icon: 'ask', askId: a.id, title: `${a.who ?? 'Un coach'} demande le ${a.weekly ? WEEKDAYS[isoDay(a.day) - 1] : sdm(a.day)} ${h(a.starts)}–${h(a.ends)}`,
      sub: `${hall(a.hall_id)} · ${a.weekly ? 'toutes les semaines' : 'une fois'}${team(a.team_id) ? ` · ${team(a.team_id)!.name}` : ''}` });
  }
  for (const l of w.losses.filter(x => x.pending > 0 && x.to_day >= w.today)) {
    const hits = w.hits.filter(h => h.loss_id === l.id);
    const tr = hits.filter(h => h.kind === 'training'), mt = hits.filter(h => h.kind === 'match');
    const when = l.from_day === l.to_day ? sdm(l.from_day).replace(/ [a-zé.]+$/, '') : `${sd(l.from_day).toLowerCase()} et ${sd(l.to_day).toLowerCase()}`;
    const parts = [tr.length ? `${tr.length} séance${tr.length > 1 ? 's' : ''} à reloger (${[...new Set(tr.map(h => teamShort(team(h.team_id)?.name ?? '')))].join(', ')})` : '',
      mt.length ? `${mt.length} match${mt.length > 1 ? 's' : ''} à domicile au bureau` : ''].filter(Boolean);
    out.push({ key: `l:${l.id}`, icon: 'loss', lossId: l.id, title: `${l.reason ?? 'Créneau perdu'} · ${when}`, sub: parts.join(' · ') || 'Rien n’est annulé d’office' });
  }
  if (off.length) {
    const names = [...new Set(off.map(o => teamShort(team(o.team_id)?.name ?? '')))].filter(Boolean);
    out.push({ key: 'coh', icon: 'off', href: '#/salles/coherence', title: `${off.length} séance${off.length > 1 ? 's' : ''} hors créneau`,
      sub: `${names.join(', ')} : à rattacher ou à marquer « Ailleurs »` });
  }
  for (const r of reqs.filter(x => x.status === 'pending')) {
    out.push({ key: `r:${r.id}`, icon: 'request', href: `#/salles/demande/${r.id}`, title: `${r.hall} · ${sdm(r.day)} ${h(r.starts)}`,
      sub: `${r.who ?? 'Un membre'} le demande${PURPOSE_FOR[r.purpose] ? ` ${PURPOSE_FOR[r.purpose]}` : r.purpose_text ? ` : ${r.purpose_text}` : ''}` });
  }
  // Les périodes d'avant (D-130) et le planning de la saison suivante : leurs souhaits, comme avant.
  for (const p of w.periods.filter(x => !x.prefill && x.back > w.today && (x.key === 'saison' || (x.mode === 'apart' && x.status === 'open')))) {
    out.push({ key: `p:${p.id}`, icon: 'period', href: p.key === 'saison' ? '#/salles/saison' : `#/salles/vacances/${p.key}/${p.season}`, title: p.label,
      sub: `Souhaits des coachs : ${p.received ?? 0} reçus sur ${p.expected ?? 0}` });
  }
  if (w.bureau && !w.calendar?.confirmed_at && (w.calendar?.academy || w.zone?.zone)) {
    out.push({ key: 'zone', icon: 'zone', href: '#/salles/reglages/zone', title: 'L’académie du club à confirmer',
      sub: `${w.calendar?.academy ? `Académie de ${w.calendar.academy}` : `Zone ${w.zone.zone}`}, d’après le département : les dates des vacances en dépendent` });
  }
  return out;
}

// --- Les vacances en 3 étapes ------------------------------------------------------------------------------------------
const between = (from: string, to: string) => { const out: string[] = []; for (let d = from; d < to; d = addDays(d, 1)) out.push(d); return out; };
/** Les jours des vacances (de la fin des cours à la veille de la reprise). */
export const vacDays = (h: { starts: string; back: string }) => between(h.starts, h.back);

/** Étape 1, une salle : ses créneaux d'une semaine, combien pendant ces vacances (hors fériés) et lesquels n'ont pas lieu. */
export function hallVacCount(w: Pick<Week, 'slots' | 'season'>, view: Pick<VView, 'phalls' | 'ranges' | 'feries'>, periodId: string | null,
  h: { starts: string; back: string }, hallId: string) {
  const days = vacDays(h);
  const slots = w.slots.filter(s => s.hall_id === hallId && s.team_id);
  const perWeek = w.slots.filter(s => s.hall_id === hallId && s.season === w.season).length;
  let held = 0, lost = 0;
  const teams = new Set<string>();
  for (const d of days) {
    for (const s of slots.filter(x => x.weekday === isoDay(d) && x.season === seasonOfDay(d))) {
      if (view.feries.some(f => f.day === d) && !view.phalls.some(x => x.hall_id === hallId && x.open_days.includes(d))) continue;
      teams.add(s.team_id!);
      if (vacValid(view.phalls, view.ranges, view.feries, periodId, hallId, d, s.starts.slice(0, 5), s.ends.slice(0, 5))) held++; else lost++;
    }
  }
  return { perWeek, held, lost, teams: [...teams] };
}
const seasonOfDay = (d: string) => { const y = Number(d.slice(0, 4)), m = Number(d.slice(5, 7)); return m >= 7 ? `${y}-${y + 1}` : `${y - 1}-${y}`; };

/** Étape 2, côté responsable : une ligne par équipe attendue (gardés, retirés, ajouts ; confirmé ou pas encore). */
export function coachLines(view: VView): { team: VTeam; kept: number; out: { day: string }[]; adds: VWish[]; answered: boolean }[] {
  return view.teams.filter(t => t.expected).map(t => {
    const ws = view.wishes.filter(x => x.team_id === t.id);
    return { team: t, kept: ws.filter(x => x.habit && x.status === 'accepted').length, out: ws.filter(x => x.habit && x.status === 'withdrawn').map(x => ({ day: x.day })),
      adds: ws.filter(x => !x.habit && x.status !== 'withdrawn'), answered: !!t.answered_at };
  });
}
/** Étape 3 : ce qui passe tel quel (créneaux habituels gardés), les retirés, les ajouts et leurs chevauchements. */
export function arbitration(view: VView) {
  const kept = view.wishes.filter(x => x.habit && x.status === 'accepted');
  const out = view.wishes.filter(x => x.habit && x.status === 'withdrawn');
  const adds = view.wishes.filter(x => !x.habit && x.status !== 'withdrawn');
  const open = adds.filter(x => x.status === 'sent');
  return { kept, out, adds, open };
}
